<template>
  <div class="flex-fill mt-3" style="min-height: 100%; height: 100%">
    <v-simple-table ref="itemsTable" class="summary-footer" dense>
      <template v-slot:default>
        <thead>
          <tr>
            <th style="width: 0">#</th>
            <th style="width: 30%">{{ $t("product-name") }}</th>
            <th>{{ $t("unit") }}</th>
            <th>{{ $t("value") }}</th>
            <th>{{ $t("fee") + " " + "(" + $store.state.currency.symbol + ")" }}</th>
            <th>{{ $t("discount") + " " + "(" + $store.state.currency.symbol + ")" }}</th>
            <th>{{ $t("tax") + " " + "(" + $store.state.currency.symbol + ")" }}</th>
            <th>{{ $t("sum") + " " + "(" + $store.state.currency.symbol + ")" }}</th>
            <th style="width: 0">{{ $t("operation") }}</th>
          </tr>
        </thead>
        <tbody ref="refBody">
          <tr
            v-for="(item, index) in items"
            :key="index"
            v-if="item.product"
            :class="{ 'has-error': errorIndexes?.includes(index) }"
            style="cursor: pointer"
          >
            <td
              style="border-radius: 0"
              @click="!isSellPriceLocked && openProductEdit(item)"
            >
              {{ +index + +1 }}
            </td>
            <td @click="!isSellPriceLocked && openProductEdit(item)">
              <div class="d-flex align-center gap-1">
                <img
                  :src="
                    item.product?.avatar?.link || require('@/assets/imagePlacholder.png')
                  "
                  class="table-img"
                />
                <v-icon v-if="!isSellPriceLocked" size="15" color="primary"
                  >mdi-pencil</v-icon
                >
                <span style="color: #0066ff">
                  {{
                    item.product.name.length > 25
                      ? item.product.name.substring(0, 22) +
                        (item.product.code ? " (" + item.product.code + ") " : "") +
                        "..."
                      : item.product.name +
                        (item.product.code ? " (" + item.product.code + ") " : "")
                  }}
                </span>
              </div>
            </td>
            <td @click="!isSellPriceLocked && openProductEdit(item)">
              {{ item.product.unit ? item.product.unit.fame : "" }}
            </td>
            <td
              class="inline-edit-cell"
              @click="
                !isInlineEditing(item, 'count') &&
                  handleEditableCellClick(item, $event, 'count')
              "
              @dblclick.stop.prevent="handleEditableCellDblClick(item, 'count')"
            >
              <span
                class="inline-cell-placeholder"
                :class="{
                  'inline-cell-placeholder--hidden': isInlineEditing(item, 'count'),
                }"
              >
                {{ item.count ? item.count : 0 }}
              </span>
              <input
                v-show="isInlineEditing(item, 'count')"
                v-model="inlineEditValue"
                type="text"
                class="inline-cell-input"
                @click.stop
                @focus="$event.target.select()"
                @keydown.enter.prevent="commitInlineEdit"
                @keydown.esc.prevent="cancelInlineEdit"
                @blur="commitInlineEdit"
              />
            </td>
            <td
              class="inline-edit-cell"
              @click="
                canInlineEditField('fee') &&
                  !isInlineEditing(item, 'fee') &&
                  handleEditableCellClick(item, $event, 'fee')
              "
              @dblclick.stop.prevent="
                canInlineEditField('fee') && handleEditableCellDblClick(item, 'fee')
              "
            >
              <span
                class="inline-cell-placeholder"
                :class="{
                  'inline-cell-placeholder--hidden': isInlineEditing(item, 'fee'),
                }"
              >
                {{ item.fee != 0 && item.fee != null ? $numberFormat(item.fee) : 0 }}
              </span>
              <input
                v-show="isInlineEditing(item, 'fee')"
                v-model="inlineEditValue"
                type="text"
                class="inline-cell-input"
                @click.stop
                @focus="$event.target.select()"
                @keydown.enter.prevent="commitInlineEdit"
                @keydown.esc.prevent="cancelInlineEdit"
                @blur="commitInlineEdit"
              />
            </td>
            <td
              class="inline-edit-cell"
              @click="
                canInlineEditField('discount') &&
                  !isInlineEditing(item, 'discount') &&
                  handleEditableCellClick(item, $event, 'discount')
              "
              @dblclick.stop.prevent="
                canInlineEditField('discount') &&
                  handleEditableCellDblClick(item, 'discount')
              "
            >
              <span
                class="inline-cell-placeholder"
                :class="{
                  'inline-cell-placeholder--hidden': isInlineEditing(item, 'discount'),
                }"
              >
                {{
                  (item.discount != 0 && item.discount != null) ||
                  (item.discountPercent != 0 && item.discountPercent != null)
                    ? "(" +
                      formatRowDiscountPercent(item.discountPercent) +
                      "%) " +
                      $numberFormat(item.discount || 0)
                    : 0
                }}
              </span>
              <input
                v-show="isInlineEditing(item, 'discount')"
                v-model="inlineEditValue"
                type="text"
                class="inline-cell-input"
                @click.stop
                @focus="$event.target.select()"
                @keydown.enter.prevent="commitInlineEdit"
                @keydown.esc.prevent="cancelInlineEdit"
                @blur="commitInlineEdit"
              />
            </td>
            <td @click="!isSellPriceLocked && openProductEdit(item)">
              {{
                item.tax != null && item.tax != 0 ? " (" + item.tax_percent + "%) " : ""
              }}
              {{ $numberFormat(item.tax) }}
            </td>
            <td @click="!isSellPriceLocked && openProductEdit(item)">
              {{
                parseFloat(item.fee || 0) * parseFloat(item.count || 0) +
                  parseFloat(isNaN(item.tax) ? 0 : item.tax) -
                  parseFloat(item.discount || 0) ===
                0
                  ? 0
                  : $numberFormat(
                      (
                        parseFloat(item.fee || 0) * parseFloat(item.count || 0) +
                        parseFloat(isNaN(item.tax) ? 0 : item.tax) -
                        parseFloat(item.discount || 0)
                      ).toFixed(decimal)
                    )
              }}
            </td>
            <td>
              <v-menu offset-y>
                <template v-slot:activator="{ attrs, on }">
                  <v-btn color="primary" icon small v-bind="attrs" v-on="on" @click.stop>
                    <v-icon size="18"> mdi-dots-vertical </v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item
                    v-if="showSerialFeature && rowHasSerial(item)"
                    link
                    @click="$emit('serial-click', item)"
                  >
                    <v-list-item-icon>
                      <v-icon color="primary">mdi-barcode</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title v-text="$t('serial-management')" />
                  </v-list-item>
                  <v-list-item v-if="scale" link @click="getItemWeight(item)">
                    <v-list-item-icon>
                      <v-icon color="blue">mdi-scale</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title v-text="$t('getWeightScale')" />
                  </v-list-item>
                  <v-list-item link @click="openPermissionDialog(item)">
                    <v-list-item-icon>
                      <v-icon color="pink"> mdi-trash-can-outline </v-icon>
                    </v-list-item-icon>
                    <v-list-item-title v-text="$t('remove')" />
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <v-dialog
      v-model="numpadDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      width="325"
    >
      <v-card style="background-color: #e3e3e3">
        <v-col class="d-flex pt-2 pb-0">
          <v-btn icon @click="numpadDialog = false">
            <v-icon class="close-hover" large color="grey"> mdi-close </v-icon>
          </v-btn>
        </v-col>
        <v-card-text class="pa-0">
          <div class="overflow-y-auto ma-1 mx-0 mt-0">
            <numpad :input="numpadValue" @save="setNumpad" />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!--permission-->
    <v-dialog
      v-model="askPermissionDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      width="300"
    >
      <v-card class="pa-5">
        <v-container class="pa-0 d-flex justify-center">
          <v-icon size="70" color="warning">mdi-alert-circle-outline</v-icon>
        </v-container>
        <v-card-text class="pa-0 text-body-1 text-center my-4">
          {{ $t("are-you-sure-delete-this-case") }}
        </v-card-text>
        <div v-if="deleteItem">
          <v-container class="pa-0 d-flex align-center gap-1 mb-1">
            <v-card-text class="pa-0 text-body-1">{{ $t("product-name") }} :</v-card-text>
            <v-card-text class="pa-0 text-body-2" style="color: #040404a3">
              {{
                deleteItem.product?.name
                  ? deleteItem.product.name.length > 25
                    ? deleteItem.product.name.substring(0, 25) + "..."
                    : deleteItem.product.name
                  : $t("haveNot")
              }}
            </v-card-text>
          </v-container>
          <v-container class="pa-0 d-flex align-center gap-1 mb-1">
            <v-card-text class="pa-0 text-body-1">{{ $t("unit") }} :</v-card-text>
            <v-card-text class="pa-0 text-body-2" style="color: #040404a3">
              {{
                deleteItem.product?.unit ? deleteItem.product.unit.fame : $t("haveNot")
              }}
            </v-card-text>
          </v-container>
          <v-container class="pa-0 d-flex align-center gap-1 mb-1">
            <v-card-text class="pa-0 text-body-1">{{ $t("count") }} :</v-card-text>
            <v-card-text class="pa-0 text-body-2" style="color: #040404a3">
              {{ deleteItem.count ? deleteItem.count : $t("haveNot") }}
            </v-card-text>
          </v-container>
          <v-container class="pa-0 d-flex align-center gap-1">
            <v-card-text class="pa-0 text-body-1">{{ $t("fee") }} :</v-card-text>
            <v-card-text class="pa-0 text-body-2" style="color: #040404a3">
              {{
                deleteItem.product?.price
                  ? $numberFormat(deleteItem.product.price) +
                    " " +
                    $store.state.currency.symbol
                  : $t("haveNot")
              }}
            </v-card-text>
          </v-container>
        </div>
        <v-container class="pa-0 d-flex justify-center align-center gap-2 mt-4">
          <v-btn color="primary" height="32" @click="remove">
            {{ $t("yes") }}
          </v-btn>
          <v-btn color="error" height="32" @click="askPermissionDialog = false">
            {{ $t("no") }}
          </v-btn>
        </v-container>
      </v-card>
    </v-dialog>
    <product-dialog
      v-model="showProductDialog"
      :item="selectedItem"
      :decimal="decimal"
      :selectedTaxOption="selectedTaxOption"
      @setItem="setEditItem"
      @addCount="addCount"
      @minusCount="minusCount"
      @zeroCountCheck="zeroCountCheck"
      @checkCount="checkCount"
    />
    <fee-calculator-modal
      v-if="feeCalculatorEnabled"
      v-model="feeCalculatorDialog"
      :initial-value="feeCalculatorValue"
      :field-type="feeCalculatorField"
      :unit-label="feeCalculatorUnitLabel"
      :anchor-point="feeCalculatorAnchorPoint"
      @confirm="applyCalculatorValue"
    />
  </div>
</template>

<script>
import General from "@/Mixins/general";
import FeeCalculatorModal from "./FeeCalculatorModal.vue";
export default {
  name: "IndexCategoryItems",
  components: {
    numpad: () => import("../../../components/general/numpad/index.vue"),
    FeeCalculatorModal,
  },
  mixins: [General],
  props: [
    "itemsProp",
    "decimal",
    "currency",
    "branch",
    "account",
    "type",
    "offline",
    "scale",
    "weight",
    "errorIndexes",
    "selectedTaxOption",
    "feeCalculatorEnabled",
    "isEdit",
    "priceType",
  ],
  data() {
    return {
      items: [],
      numpadDialog: false,
      loading: false,
      selectedItem: null,
      selectedItemNumpad: null,
      showProductDialog: false,
      isLoading: false,
      numpadValue: 0,
      timer: 0,
      appendTimer: 0,
      deleteItem: null,
      askPermissionDialog: false,
      feeCalculatorDialog: false,
      feeCalculatorValue: 0,
      feeCalculatorField: "fee",
      feeCalculatorUnitLabel: "",
      feeCalculatorAnchorPoint: null,
      selectedFeeItem: null,
      inlineEditItem: null,
      inlineEditField: null,
      inlineEditValue: "",
      editableCellClickTimer: null,
      prevItemsLength: 0,
    };
  },
  computed: {
    isSellPriceLocked() {
      return (
        (this.type === "sell" || this.type === "presale") &&
        this.$store.state.allConfig &&
        this.$store.state.allConfig.userInt &&
        this.$store.state.allConfig.userInt.deactiveChangeSellPrice === "active"
      );
    },
    canInlineEditField() {
      return (field) => !this.isSellPriceLocked || field === "count";
    },
    shouldApplyPricing() {
      return this.priceType === "formulaPrice";
    },
    //add serial
    showSerialFeature() {
      return ["purchase", "sell", "returnPurchase", "returnSell", "presale"].includes(
        this.type
      );
    },
  },
  watch: {
    itemsProp(val) {
      this.items = val;
    },
    decimal() {},
    currency() {},
    branch(val) {
      if (!this.shouldApplyPricing) {
        return;
      }
      if (val && this.items && this.items.length > 0) {
        this.items.forEach((item) => {
          if (item.product && item.product.pricing && item.product.pricing.length > 0) {
            this.applyPricingToRow(item, val, this.currency, this.account);
          }
        });
        this.calcSum();
      }
    },
    account(val) {
      if (!this.shouldApplyPricing) {
        return;
      }
      if (this.items && this.items.length > 0) {
        this.items.forEach((item) => {
          if (item.product && item.product.pricing && item.product.pricing.length > 0) {
            this.applyPricingToRow(item, this.branch, this.currency, val);
          }
        });
        this.calcSum();
      }
    },
    type() {},
    offline() {},
    askPermissionDialog(val) {
      if (val) this.closeCalculator();
    },
    showProductDialog(val) {
      if (val) {
        this.closeCalculator();
        this.$store.commit("SET_AUTOFOCUS", false);
      } else {
        this.$store.commit("SET_AUTOFOCUS", true);
        this.$emit("setinputFocus");
        if (this.selectedItem) {
          this.$emit("checkDiscountPercent", this.selectedItem);
          this.calcSum();
        }
      }
    },
    items: {
      handler() {
        this.$emit("input", this.items);
        this.$emit("callrefbodyhight", this.$refs.refBody?.scrollHeight);

        const length = this.items?.filter((item) => item.product).length || 0;
        if (length > this.prevItemsLength) {
          this.$nextTick(() => {
            const wrapper = this.$refs.itemsTable?.$el?.querySelector(
              ".v-data-table__wrapper"
            );
            if (!wrapper) return;
            if (wrapper.scrollHeight > wrapper.clientHeight) {
              wrapper.scrollTo({
                top: wrapper.scrollHeight,
                behavior: "smooth",
              });
            }
          });
        }
        this.prevItemsLength = length;
      },
      deep: true,
    },
  },
  mounted() {
    if (this.itemsProp) {
      this.items = this.itemsProp;
    }
  },
  methods: {
    formatRowDiscountPercent(percent) {
      const n = Math.floor(parseFloat(percent || 0) * 100) / 100;
      return parseFloat(n.toFixed(2));
    },
    closeCalculator() {
      this.feeCalculatorDialog = false;
      this.numpadDialog = false;
    },
    openPermissionDialog(item) {
      this.closeCalculator();
      this.deleteItem = item;
      this.askPermissionDialog = true;
    },
    rowHasSerial(item) {
      if (!item) return false;
      if (item.has_serial === "active") return true;
      const product = item.product;
      if (!product) return false;
      const flag = (product.meta && product.meta.has_serial) || product.has_serial;
      return flag === "active";
    },
    remove() {
      var i = this.items.indexOf(this.deleteItem);
      this.items.splice(i, 1);
      this.calcSum();
      this.askPermissionDialog = false;
      this.$emit("itemRemoved", i);
    },
    setEditItem(item) {
      this.selectedItem.product = item.product;
      this.selectedItem.fee = item.fee;
      this.selectedItem.discountPercent = item.discountPercent;
      this.selectedItem.discount = item.discount;
      this.selectedItem.tax_percent = item.tax_percent;
      if ((parseFloat(item.discount) || 0) > 0) {
        this.$emit("checkDiscount", this.selectedItem);
      } else {
        this.$emit("checkDiscountPercent", this.selectedItem);
      }
      this.calcSum();
    },
    openProductEdit(item) {
      if (this.isSellPriceLocked) return;
      if (
        this.$store.state.permissions &&
        this.$store.state.permissions.includes("ProductEdit")
      ) {
        this.closeCalculator();
        this.selectedItem = item;
        this.showProductDialog = true;
      }
    },
    isInlineEditing(item, field) {
      return this.inlineEditItem === item && this.inlineEditField === field;
    },
    getInlineEditValue(item, field) {
      if (field === "count") {
        return item.count != null && item.count !== "" ? String(item.count) : "0";
      }
      if (field === "discount") {
        return item.discount != null && item.discount !== ""
          ? String(item.discount)
          : "0";
      }
      return item.fee != null && item.fee !== "" ? String(item.fee) : "0";
    },
    startInlineEdit(item, field) {
      if (!this.canInlineEditField(field)) return;
      if (this.inlineEditItem && !this.isInlineEditing(item, field)) {
        this.commitInlineEdit();
      }
      this.inlineEditItem = item;
      this.inlineEditField = field;
      this.inlineEditValue = this.getInlineEditValue(item, field);
      this.$nextTick(() => {
        const inputs = this.$el.querySelectorAll(".inline-cell-input");
        const input = Array.from(inputs).find((el) => el.offsetParent !== null);
        if (input) {
          input.focus();
          input.select();
        }
      });
    },
    clearInlineEdit() {
      this.inlineEditItem = null;
      this.inlineEditField = null;
      this.inlineEditValue = "";
    },
    commitInlineEdit() {
      if (!this.inlineEditItem || !this.inlineEditField) return;
      const normalized = String(this.inlineEditValue).replace(/,/g, "").trim();
      this.applyFieldValue(this.inlineEditItem, this.inlineEditField, normalized);
      this.clearInlineEdit();
    },
    cancelInlineEdit() {
      this.clearInlineEdit();
    },
    applyFieldValue(item, field, value) {
      const amount = parseFloat(value || 0);

      if (field === "count") {
        item.count = isNaN(amount) ? 0 : amount;
        if (parseFloat(item.count) === 0) {
          item.count = 1;
        }
        this.checkCount(item);
        return;
      }

      if (field === "discount") {
        item.discount = isNaN(amount) ? 0 : amount;
        this.$emit("checkDiscount", item);
        this.calcSum();
        return;
      }

      if (field === "tax") {
        if (this.selectedTaxOption === "active") {
          const base =
            parseFloat(item.fee || 0) * parseFloat(item.count || 0) -
            parseFloat(item.discount || 0);
          const taxAmount = isNaN(amount) ? 0 : amount;
          if (base > 0) {
            item.tax_percent = parseFloat(
              ((taxAmount / base) * 100).toFixed(this.decimal)
            );
          } else {
            item.tax_percent = 0;
          }
          item.tax = taxAmount.toFixed(this.decimal);
        } else {
          item.tax = 0;
          item.tax_percent = 0;
        }
        this.calcSum();
        return;
      }

      item.fee = isNaN(amount) ? 0 : amount;
      this.calcSum();
    },
    handleEditableCellClick(item, event, field) {
      if (!this.canInlineEditField(field)) return;
      if (this.feeCalculatorEnabled) {
        if (field === "fee" || field === "discount" || field === "count") {
          clearTimeout(this.editableCellClickTimer);
          this.editableCellClickTimer = setTimeout(() => {
            this.openFeeCalculator(item, event, field);
          }, 220);
          return;
        }
        this.openProductEdit(item);
        return;
      }
      this.startInlineEdit(item, field);
    },
    handleEditableCellDblClick(item, field) {
      if (!this.canInlineEditField(field)) return;
      if (!this.feeCalculatorEnabled) return;
      clearTimeout(this.editableCellClickTimer);
      if (this.feeCalculatorDialog) {
        this.feeCalculatorDialog = false;
      }
      this.startInlineEdit(item, field);
    },
    getItemUnitLabel(item) {
      const unit = item?.unit || item?.product?.unit;
      if (!unit) return "";
      return unit.fame || unit.name || unit.title || "";
    },
    openFeeCalculator(item, event, field = "fee") {
      if (!this.canInlineEditField(field)) return;
      clearTimeout(this.editableCellClickTimer);
      this.numpadDialog = false;
      this.selectedFeeItem = item;
      this.feeCalculatorField = field;
      this.feeCalculatorUnitLabel = field === "count" ? this.getItemUnitLabel(item) : "";
      if (field === "discount") {
        this.feeCalculatorValue = item && item.discount != null ? item.discount : 0;
      } else if (field === "count") {
        this.feeCalculatorValue = item && item.count != null ? item.count : 0;
      } else if (field === "tax") {
        this.feeCalculatorValue = item && item.tax != null ? item.tax : 0;
      } else {
        this.feeCalculatorValue = item && item.fee != null ? item.fee : 0;
      }
      this.feeCalculatorAnchorPoint = event
        ? { x: event.clientX, y: event.clientY }
        : null;
      this.feeCalculatorDialog = true;
    },
    applyCalculatorValue(value) {
      if (!this.selectedFeeItem) return;
      this.applyFieldValue(this.selectedFeeItem, this.feeCalculatorField, value);
    },
    feeValidate(value) {
      if (!value || parseFloat(value) <= 0) {
        return true;
        // if (this.showProductDialog) {
        //   return true
        // } else {
        //   return this.$t('is-required')
        // }
      } else {
        return true;
      }
    },
    openNumpad(item) {
      this.feeCalculatorDialog = false;
      this.selectedItemNumpad = item;
      this.numpadValue = item.count;
      this.numpadDialog = true;
    },
    setNumpad(data) {
      this.numpadDialog = false;
      this.selectedItemNumpad.count = data;
      this.calcSum();
    },
    addCount(item) {
      item.count++;
      var app = this;
      clearTimeout(this.appendTimer);
      this.appendTimer = setTimeout(function () {
        app.calcSum();
      }, 100);
    },
    minusCount(item) {
      item.count--;
      var app = this;
      clearTimeout(this.appendTimer);
      this.appendTimer = setTimeout(function () {
        app.calcSum();
      }, 100);
    },
    zeroCountCheck(item) {
      if (parseFloat(item.count) === 0) {
        item.count = 1;
        this.checkCount(item);
      }
    },
    findMatchedPricingRule(product, count, branch, currency, categoryId, ruleType) {
      const pricing = product?.pricing;
      if (!pricing || pricing.length === 0) {
        return null;
      }
      const branchId = branch?.id ?? null;
      const currencyId = currency?.id ?? null;
      const accountCategoryId = categoryId ?? null;
      const qty = parseFloat(count || 0);
      const findRule = (targetCategoryId) =>
        pricing.find((rule) => {
          if (rule.type !== ruleType) {
            return false;
          }
          const ruleCategoryId = rule.category_id ?? rule.category?.id ?? null;
          if (ruleCategoryId !== targetCategoryId) {
            return false;
          }
          const ruleBranchId = rule.branch_id ?? rule.branch?.id ?? null;
          if (ruleBranchId != null && ruleBranchId !== branchId) {
            return false;
          }
          const ruleCurrencyId = rule.currency_id ?? rule.currency?.id ?? null;
          if (
            ruleCurrencyId != null &&
            currencyId != null &&
            ruleCurrencyId !== currencyId
          ) {
            return false;
          }
          const min =
            rule.min != null && rule.min !== "" ? parseFloat(rule.min) : -Infinity;
          const max =
            rule.max != null && rule.max !== "" ? parseFloat(rule.max) : Infinity;
          return qty >= min && qty <= max;
        }) || null;

      return accountCategoryId != null
        ? findRule(accountCategoryId) || findRule(null)
        : findRule(null);
    },
    applyPricingToRow(item, branch, currency, account) {
      if (!item?.product) {
        return;
      }
      const product = item.product;
      const categoryId = account?.category_id ?? null;
      const baseFee = parseFloat(product.price || 0);
      const count = parseFloat(item.count || 0);
      item.fee = baseFee;
      const baseRowMax = baseFee * count;
      const productDisc = parseFloat(product.discount || 0);
      const productDiscPct = parseFloat(product.discount_percent || 0);
      if (productDisc > 0) {
        item.discount = Math.min(baseRowMax || productDisc, productDisc);
        item.discountPercent =
          baseRowMax > 0
            ? parseFloat(((item.discount / baseRowMax) * 100).toFixed(this.decimal))
            : 0;
      } else if (productDiscPct > 0) {
        item.discountPercent = productDiscPct;
        item.discount = parseFloat(
          (baseRowMax * (productDiscPct / 100)).toFixed(this.decimal)
        );
      } else {
        item.discount = 0;
        item.discountPercent = 0;
      }
      const priceRule = this.findMatchedPricingRule(
        product,
        item.count,
        branch,
        currency,
        categoryId,
        "price"
      );
      const discountRule = this.findMatchedPricingRule(
        product,
        item.count,
        branch,
        currency,
        categoryId,
        "discount"
      );
      if (priceRule && priceRule.price != null) {
        item.fee = parseFloat(priceRule.price);
      }
      if (discountRule) {
        const rulePrice = parseFloat(discountRule.price || 0);
        const hasPercent = discountRule.percent != null && discountRule.percent !== "";
        const currentRowMax = parseFloat(item.fee || 0) * count;
        if (hasPercent && rulePrice === 0) {
          const pct = parseFloat(discountRule.percent);
          item.discountPercent = pct;
          item.discount = parseFloat((currentRowMax * (pct / 100)).toFixed(this.decimal));
        } else if (
          (discountRule.percent == null || discountRule.percent === "") &&
          rulePrice > 0
        ) {
          item.discount = Math.min(currentRowMax, rulePrice);
          item.discountPercent =
            currentRowMax > 0
              ? parseFloat(((item.discount / currentRowMax) * 100).toFixed(this.decimal))
              : 0;
        }
      }
    },
    checkCount(item) {
      if (
        this.shouldApplyPricing &&
        item.product &&
        item.product.pricing &&
        item.product.pricing.length > 0 &&
        (this.type === "sell" ||
          this.type === "presale" ||
          this.type === "returnPurchase")
      ) {
        this.applyPricingToRow(item, this.branch, this.currency, this.account);
      }
      if (
        !this.offline &&
        (this.type === "sell" ||
          this.type === "presale" ||
          this.type === "returnPurchase")
      ) {
        var productStock =
          item.product && item.product.stock ? parseFloat(item.product.stock.count) : 0;
        if (
          item.product &&
          item.product.type === "product" &&
          this.$store.state.negative_inventory === "no" &&
          parseFloat(item.count) >
            productStock +
              parseFloat(this.$route.name !== "presaleEdit" ? item.countEdit : 0)
        ) {
          this.snakbarText = this.$t("alert-inventory-is-not-enough");
          this.snakbarColor = "pink";
          this.snakbar = true;
        } else if (
          this.shouldApplyPricing &&
          this.$route.name !== "presaleEdit" &&
          item.product &&
          !item.product.price &&
          !(item.product.pricing && item.product.pricing.length > 0)
        ) {
          if (this.type === "sell" || this.type === "presale") {
            this.getPrice(item);
          }
        }
      }
      this.calcSum();
    },
    getPrice(item) {
      if (!this.shouldApplyPricing) {
        return;
      }
      var app = this;
      clearTimeout(this.timer);
      this.timer = setTimeout(function () {
        app.$axios
          .get(app.$endpoint + "getdetail", {
            params: {
              select: "getPrice",
              currency_id: app.currency.id,
              count: item.count,
              product_id: item.product.id,
              category_id:
                app.account && app.account.category_id ? app.account.category_id : -1,
            },
          })
          .then((res) => {
            res = res.data;
            if (res.price > 0) {
              item.fee = res.price;
            } else {
              item.fee = 0;
            }
            if (item.fee > 0 && item.count > 0) {
              if (res.discount.amount) {
                item.discount = res.discount.amount;
              }
              if (res.discount.percent && parseFloat(res.discount.percent) > 0) {
                var discount =
                  parseFloat(item.fee) *
                  parseFloat(item.count) *
                  (parseFloat(res.discount.percent) / 100);
                item.discount = discount;
              }
            }
            app.calcSum();
          })
          .catch((err) => {
            app.serverErrorHandeler(err);
          })
          .finally(() => (app.isLoading = false));
      }, 500);
      this.loading = false;
    },
    calcSum() {
      this.$emit("calc");
    },
    getItemWeight(item) {
      const idsKG = [13, 44, 47, 51];
      const idsG = [14];

      if (idsKG.includes(item.product?.unit?.id)) {
        item.count = this.weight / 1000;
      } else if (idsG.includes(item.product?.unit?.id)) {
        item.count = this.weight;
      } else {
        item.count = this.weight;
      }

      this.checkCount(item);
    },
  },
};
</script>

<style scoped>
.has-error {
  background-color: rgba(255, 0, 0, 0.1) !important;
}
::v-deep .v-data-table__wrapper {
  min-height: 100%;
  overflow: auto;
  height: 100%;
}
::v-deep .v-data-table__wrapper::-webkit-scrollbar {
  display: none;
}
.summary-footer {
  min-height: 100%;
  height: 100%;
  border: 1px solid silver;
}
::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  padding: 5px 10px;
  border-left: 1px solid silver;
  font-size: 8pt;
  text-align: center;
  height: 32px !important;
  line-height: 1.2;
  background-color: #fafafae3;
  color: #5c6370;
  font-weight: 500;
}
::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th:last-child,
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td:last-child {
  border-left: none;
}
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  text-align: center;
  border-left: 1px solid silver;
}
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td:nth-child(2),
::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th:nth-child(2) {
  text-align: initial;
}
::v-deep
  .v-text-field--outlined.v-input--dense.v-text-field--outlined
  > .v-input__control
  > .v-input__slot {
  min-height: 28px !important;
}
.table-img {
  width: 35px;
  height: 35px;
  border: 1px solid #c0c0c057;
  border-radius: 4px;
}
td:not(.stiJsViewerClearAllStyles) {
  height: 45px !important;
}
.inline-edit-cell {
  position: relative;
  padding: 0 !important;
}
.inline-cell-placeholder--hidden {
  visibility: hidden;
}
.inline-cell-input {
  position: absolute;
  inset: 0;
  width: 100%;
  min-width: 0;
  height: 100%;
  border: 1px solid #1976d2;
  border-radius: 0;
  text-align: center;
  font-size: 8.5pt;
  font-weight: 600;
  color: #2f3742;
  background: #fff;
  outline: none;
  padding: 0 6px;
  box-sizing: border-box;
}
.v-btn--icon.v-size--small .v-icon {
  margin-top: 0 !important;
}
</style>
